import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { ordersAPI, authAPI } from '../api';
import AdsDisplay from '../components/AdsDisplay';
import PlatformIcon from '../components/PlatformIcon';
import {
  DollarSign,
  Package,
  CheckCircle,
  Clock,
  CreditCard,
  Plus,
  RefreshCw,
  Zap,
  MessageCircle,
  ChevronRight,
  Star,
  Phone,
  ArrowRight,
} from 'react-feather';
import { formatCurrencyWithConversion } from '../utils/currency';
import './Dashboard.css';

const STATUS_COLORS = {
  pending: 'badge-pending',
  in_progress: 'badge-in_progress',
  completed: 'badge-completed',
  partial: 'badge-partial',
  canceled: 'badge-canceled',
  refunded: 'badge-canceled',
};

function getDisplayStatus(order) {
  return order?.refund_status === 'refunded' || order?.refund_status === 'manual_refunded'
    ? 'refunded'
    : order?.status;
}

const POPULAR_SMM = [
  { id: 'instagram', name: 'Instagram', platform: 'instagram' },
  { id: 'tiktok', name: 'TikTok', platform: 'tiktok' },
  { id: 'youtube', name: 'YouTube', platform: 'youtube' },
  { id: 'facebook', name: 'Facebook', platform: 'facebook' },
  { id: 'whatsapp', name: 'WhatsApp', platform: 'whatsapp' },
  { id: 'telegram', name: 'Telegram', platform: 'telegram' },
  { id: 'twitter', name: 'Twitter / X', platform: 'twitter' },
  { id: 'spotify', name: 'Spotify', platform: 'spotify' },
];

const POPULAR_VN = [
  { id: 'usa', name: 'USA', fullName: 'United States', flag: '🇺🇸', code: '+1', countryCode: 'US' },
  { id: 'uk', name: 'UK', fullName: 'United Kingdom', flag: '🇬🇧', code: '+44', countryCode: 'GB' },
  { id: 'canada', name: 'Canada', fullName: 'Canada', flag: '🇨🇦', code: '+1', countryCode: 'CA' },
  { id: 'france', name: 'France', fullName: 'France', flag: '🇫🇷', code: '+33', countryCode: 'FR' },
  { id: 'germany', name: 'Germany', fullName: 'Germany', flag: '🇩🇪', code: '+49', countryCode: 'DE' },
  { id: 'netherlands', name: 'Netherlands', fullName: 'Netherlands', flag: '🇳🇱', code: '+31', countryCode: 'NL' },
];

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, refreshUser, publicSettings } = useAuth();
  const { t } = useLanguage();
  const [recentOrders, setRecentOrders] = useState([]);
  const [totalOrders, setTotalOrders] = useState(0);
  const [loading, setLoading] = useState(true);
  const [whatsappNum, setWhatsappNum] = useState('+237600000000');

  const vnStatus = publicSettings?.virtual_numbers_status || 'beta';
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const canAccessVirtualNumbers =
    isAdmin ||
    vnStatus === 'active' ||
    ((vnStatus === 'beta' || vnStatus === 'specific_users') && (user?.is_tester === 1 || user?.is_tester === true));

  useEffect(() => {
    authAPI.publicSettings().then(r => {
      const num = (r.data.settings?.whatsapp_number || process.env.REACT_APP_WHATSAPP || '+237600000000').replace(/\s/g, '');
      setWhatsappNum(num);
    }).catch(() => {
      setWhatsappNum((process.env.REACT_APP_WHATSAPP || '+237600000000').replace(/\s/g, ''));
    });
  }, []);

  const load = useCallback(async () => {
    try {
      const [, res] = await Promise.all([
        refreshUser(),
        ordersAPI.getRecent(),
      ]);
      setRecentOrders(res.data.orders || []);
      setTotalOrders(res.data.total || 0);
    } catch (err) {
      console.error('Dashboard load error:', err);
    } finally {
      setLoading(false);
    }
  }, [refreshUser]);

  useEffect(() => { load(); }, [load]);

  const balance = parseFloat(user?.balance || 0);
  const total = totalOrders;
  const completed = recentOrders.filter(o => getDisplayStatus(o) === 'completed').length;
  const active = recentOrders.filter(o => ['pending', 'in_progress'].includes(getDisplayStatus(o))).length;

  const getUserInitials = useMemo(() => {
    if (!user?.username) return 'NB';
    const parts = user.username.trim().split(/[\s_-]+/);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return user.username.slice(0, 2).toUpperCase();
  }, [user?.username]);

  return (
    <div className="dashboard-container">
      {/* ── 1. Top Header & Welcome Banner ── */}
      <div className="dashboard-hero-header">
        <div className="dashboard-user-greeting">
          <div className="dashboard-user-avatar">
            {getUserInitials}
          </div>
          <div>
            <h1 className="dashboard-greeting-title">
              Hello, <span>{user?.username}</span>
            </h1>
            <p className="dashboard-greeting-subtitle">
              Manage your orders, social media campaigns, and temporary numbers.
            </p>
          </div>
        </div>

        <div className="dashboard-hero-actions">
          <Link to="/dashboard/deposit" className="btn btn-outline-hero">
            <CreditCard size={17} /> Add Funds
          </Link>
          <Link to="/dashboard/orders/new" className="btn btn-primary-hero">
            <Plus size={18} /> New Order
          </Link>
        </div>
      </div>

      {/* ── 2. Action Hub Cards (Social Media & Virtual Numbers) ── */}
      <div className={`dashboard-hub-row ${!canAccessVirtualNumbers ? 'dashboard-hub-single' : ''}`}>
        <div
          className="dashboard-hub-card dashboard-hub-smm"
          onClick={() => navigate('/dashboard/services')}
        >
          <div className="dashboard-hub-icon-wrap smm-icon-wrap">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z"></path>
              <path d="M12 15l-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z"></path>
              <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0"></path>
              <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5"></path>
            </svg>
          </div>
          <div className="dashboard-hub-content">
            <div className="dashboard-hub-card-title">{t('dash_smm_services', 'Social Media Services')}</div>
            <div className="dashboard-hub-card-desc">
              {t('dash_smm_desc', 'Boost your followers, likes, views & engagement across all platforms')}
            </div>
          </div>
          <div className="dashboard-hub-chevron">
            <ChevronRight size={18} />
          </div>
        </div>

        {canAccessVirtualNumbers && (
          <div
            className="dashboard-hub-card dashboard-hub-vn"
            onClick={() => navigate('/dashboard/virtual-numbers')}
          >
            <div className="dashboard-hub-icon-wrap vn-icon-wrap">
              <Phone size={24} />
            </div>
            <div className="dashboard-hub-content">
              <div className="dashboard-hub-card-title">{t('dash_vn_title', 'Virtual Numbers')}</div>
              <div className="dashboard-hub-card-desc">
                {t('dash_vn_desc', 'Receive instant SMS verification codes with private rental numbers')}
              </div>
            </div>
            <div className="dashboard-hub-chevron">
              <ChevronRight size={18} />
            </div>
          </div>
        )}
      </div>

      {/* ── 3. Popular Social Media Services Grid ── */}
      <div className="dashboard-section-block">
        <div className="dashboard-section-head">
          <h2 className="dashboard-section-title">{t('dash_popular_smm', 'Popular Social Media Services')}</h2>
          <Link to="/dashboard/services" className="dashboard-section-link">
            {t('dash_see_all_services', 'See all services')} <ArrowRight size={14} />
          </Link>
        </div>

        <div className="dashboard-popular-smm-grid">
          {POPULAR_SMM.map((item) => (
            <div
              key={item.id}
              className="dashboard-popular-smm-card"
              onClick={() => navigate(`/dashboard/orders/new?category=${encodeURIComponent(item.name)}`)}
            >
              <PlatformIcon platform={item.platform} size={50} iconSize={26} />
              <span className="dashboard-popular-smm-label">{item.name}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── 4. Popular Virtual Numbers Grid (Conditional) ── */}
      {canAccessVirtualNumbers && (
        <div className="dashboard-section-block">
          <div className="dashboard-section-head">
            <h2 className="dashboard-section-title">{t('dash_popular_vn', 'Popular Virtual Numbers')}</h2>
            <Link to="/dashboard/virtual-numbers" className="dashboard-section-link">
              {t('dash_browse_countries', 'Browse all countries')} <ArrowRight size={14} />
            </Link>
          </div>

          <div className="dashboard-popular-vn-grid">
            {POPULAR_VN.map((country) => (
              <div
                key={country.id}
                className="dashboard-popular-vn-card"
                onClick={() => navigate('/dashboard/virtual-numbers')}
              >
                <div className="dashboard-vn-flag-circle">
                  <span>{country.flag}</span>
                </div>
                <div className="dashboard-vn-country-info">
                  <span className="dashboard-vn-country-name">{country.name}</span>
                  <span className="dashboard-vn-country-dial">{country.code}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── 5. Quick Stats Row ── */}
      <div className="dashboard-stats-grid">
        <div className="dashboard-stat-card stat-balance-card">
          <div className="stat-card-top">
            <div className="stat-icon-pill balance-pill">
              <DollarSign size={20} />
            </div>
            <Link to="/dashboard/deposit" className="stat-topup-link">
              {t('dash_top_up', '+ Top up')}
            </Link>
          </div>
          <div className="stat-card-value">
            {formatCurrencyWithConversion(balance, user?.currency || 'USD')}
          </div>
          <div className="stat-card-label">{t('available_balance', 'Account Balance')}</div>
        </div>

        <div className="dashboard-stat-card">
          <div className="stat-card-top">
            <div className="stat-icon-pill orders-pill">
              <Package size={20} />
            </div>
          </div>
          <div className="stat-card-value">{loading ? '…' : total}</div>
          <div className="stat-card-label">{t('dash_total_orders', 'Total Orders')}</div>
        </div>

        <div className="dashboard-stat-card">
          <div className="stat-card-top">
            <div className="stat-icon-pill completed-pill">
              <CheckCircle size={20} />
            </div>
          </div>
          <div className="stat-card-value">{loading ? '…' : completed}</div>
          <div className="stat-card-label">{t('dash_all_time_completed', 'Completed Orders')}</div>
        </div>

        <div className="dashboard-stat-card">
          <div className="stat-card-top">
            <div className="stat-icon-pill active-pill">
              <Clock size={20} />
            </div>
          </div>
          <div className="stat-card-value">{loading ? '…' : active}</div>
          <div className="stat-card-label">{t('status_in_progress', 'In Progress')}</div>
        </div>
      </div>

      {/* ── 6. Fast & Reliable Feature Banner ── */}
      <div className="dashboard-feature-banner" onClick={() => navigate('/dashboard/orders/new')}>
        <div className="feature-banner-icon">
          <Star size={22} />
        </div>
        <div className="feature-banner-text">
          <div className="feature-banner-title">{t('dashboard.fast_and_reliable', 'Fast & Automated Delivery')}</div>
          <div className="feature-banner-sub">
            {t('dashboard.fast_and_reliable_sub', 'All orders start processing automatically within seconds of submission.')}
          </div>
        </div>
        <button className="btn btn-primary btn-sm feature-banner-btn">
          {t('dashboard.order_now', 'Start Order')} <ArrowRight size={14} style={{ marginLeft: 6 }} />
        </button>
      </div>

      {/* Ad Banner placement */}
      <AdsDisplay adSlot="banner-300x250" className="medium-rectangle" />

      {/* ── 7. Recent Orders Section ── */}
      <div className="dashboard-card-wrap">
        <div className="dashboard-recent-header">
          <div>
            <h3 className="dashboard-recent-title">{t('dash_recent_orders', 'Recent Orders')}</h3>
            <p className="dashboard-recent-subtitle">{t('dash_recent_subtitle', 'Track your latest service orders and delivery status')}</p>
          </div>
          <div className="dashboard-recent-actions">
            <button
              className="btn btn-secondary btn-sm dashboard-recent-btn"
              onClick={load}
              disabled={loading}
              title="Refresh recent orders"
            >
              <RefreshCw size={14} className={loading ? 'spin-icon' : ''} /> {t('dash_refresh', 'Refresh')}
            </button>
            <Link to="/dashboard/orders" className="btn btn-primary btn-sm dashboard-recent-btn">
              {t('dash_view_all_orders', 'View All Orders →')}
            </Link>
          </div>
        </div>

        {loading ? (
          <div className="dashboard-loading-state">
            <div className="spinner" style={{ margin: '0 auto 12px' }} />
            <p>{t('loading', 'Loading your orders…')}</p>
          </div>
        ) : recentOrders.length === 0 ? (
          <div className="dashboard-empty-orders">
            <div className="empty-icon-circle">
              <Package size={36} />
            </div>
            <h4>{t('no_orders_placed', 'No orders placed yet')}</h4>
            <p>{t('no_orders_desc', 'Select a service from our catalog to boost your channels today.')}</p>
            <Link to="/dashboard/orders/new" className="btn btn-primary btn-sm" style={{ marginTop: 14 }}>
              <Zap size={15} style={{ marginRight: 6 }} /> {t('place_first_order', 'Place First Order')}
            </Link>
          </div>
        ) : (
          <div className="table-wrap dashboard-recent-table-wrap">
            <table>
              <thead>
                <tr>
                  <th>{t('col_order', 'Order')}</th>
                  <th>{t('col_service', 'Service')}</th>
                  <th>{t('col_quantity', 'Quantity')}</th>
                  <th>{t('col_cost', 'Cost')}</th>
                  <th>{t('col_status', 'Status')}</th>
                  <th>{t('col_date', 'Date')}</th>
                </tr>
              </thead>
              <tbody>
                {recentOrders.slice(0, 10).map((o) => {
                  const displayStatus = getDisplayStatus(o);
                  return (
                    <tr key={o.id}>
                      <td style={{ fontFamily: 'monospace', fontWeight: 600, color: 'var(--text-muted)' }}>
                        #{o.id}
                      </td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <PlatformIcon platform={o.service_name || o.category || ''} size={30} iconSize={16} />
                          <div className="dashboard-recent-service-name" title={o.service_name}>
                            {o.service_name}
                          </div>
                        </div>
                      </td>
                      <td style={{ fontWeight: 600 }}>{parseInt(o.quantity, 10).toLocaleString()}</td>
                      <td style={{ color: 'var(--primary, #0062FF)', fontWeight: 700 }}>
                        {formatCurrencyWithConversion(o.cost, user?.currency || 'USD', 4)}
                      </td>
                      <td>
                        <span className={`badge ${STATUS_COLORS[displayStatus] || 'badge-pending'}`}>
                          {displayStatus.replace('_', ' ')}
                        </span>
                      </td>
                      <td style={{ color: 'var(--text-muted)', fontSize: 13 }}>
                        {new Date(o.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── 8. Live Support Floating / Footer Card ── */}
      <div className="dashboard-support-card">
        <div className="support-card-content">
          <div className="support-icon-circle">
            <MessageCircle size={24} color="#25D366" />
          </div>
          <div>
            <div className="support-title">{t('support.title', 'Need Help or Custom Orders?')}</div>
            <div className="support-desc">{t('support.subtitle', 'Our 24/7 dedicated support team is available on WhatsApp to assist you.')}</div>
          </div>
        </div>
        <a
          href={`https://wa.me/${whatsappNum}?text=Hello%20NiceBooster%20Support!`}
          target="_blank"
          rel="noreferrer"
          className="btn btn-whatsapp"
        >
          <MessageCircle size={16} style={{ marginRight: 6 }} /> {t('support.whatsapp_chat', 'Chat on WhatsApp')}
        </a>
      </div>
    </div>
  );
}
